import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Activity, RefreshCw, ArrowRight, Users, CheckCircle2, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import Portal from "@/components/Portal";

export default function NurseVitals() {
  const { branch } = useAuth();
  const [queue, setQueue] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [qRes, docsRes] = await Promise.all([
        api.get("/queue/nursing", { params: { branch_code: branch.code } }),
        api.get("/doctors", { params: { branch_code: branch.code } }),
      ]);
      setQueue(qRes.data);
      setDoctors(docsRes.data);
    } catch (err) {
      toast.error(formatApiError(err, "Could not load queue"));
    } finally {
      setLoading(false);
    }
  }, [branch?.code]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const openVitals = (p) => {
    setSelected(p);
    // Pre-fill doctor if reception already routed the patient
    setForm({ ...emptyForm, assigned_doctor: p.assigned_doctor || "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.assigned_doctor) {
      toast.error("Assign the patient to a doctor");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        bp_systolic: form.bp_systolic ? Number(form.bp_systolic) : null,
        bp_diastolic: form.bp_diastolic ? Number(form.bp_diastolic) : null,
        pulse: form.pulse ? Number(form.pulse) : null,
        respiratory_rate: form.respiratory_rate ? Number(form.respiratory_rate) : null,
        temp_c: form.temp_c ? Number(form.temp_c) : null,
        spo2: form.spo2 ? Number(form.spo2) : null,
        weight_kg: form.weight_kg ? Number(form.weight_kg) : null,
        height_cm: form.height_cm ? Number(form.height_cm) : null,
        notes: form.notes || "",
        assigned_doctor: form.assigned_doctor,
      };
      await api.post(`/patients/${selected.id}/vitals`, payload);
      const doctorName = doctors.find((d) => d.username === form.assigned_doctor)?.name;
      toast.success(`Sent to ${doctorName || form.assigned_doctor}`);
      setSelected(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Failed to save vitals"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto animate-fade-up" data-testid="nurse-vitals-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-[#5C5652] mb-3 ornament-dot">
            Nursing · {branch.name}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-forest mb-1">
            Vitals queue
          </h1>
          <p className="text-[#5C5652]">
            Record vitals for each patient and route them to a doctor.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="nurse-refresh"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-sand bg-white hover:bg-[#F9F6F0] transition-colors text-sm font-semibold"
        >
          <RefreshCw className="h-4 w-4" strokeWidth={1.75} />
          Refresh
        </button>
      </div>

      <div className="mb-6 grid grid-cols-2 sm:grid-cols-3 gap-4">
        <StatMini label="Awaiting vitals" value={queue.length} icon={Activity} />
        <StatMini label="Doctors at branch" value={doctors.length} icon={Users} />
        <StatMini label="Branch" value={branch.code} icon={CheckCircle2} />
      </div>

      <div className="bg-white border border-sand rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-sm text-[#5C5652]">Loading queue…</div>
        ) : queue.length === 0 ? (
          <div className="p-10 text-center">
            <div className="font-display text-forest text-xl mb-1">Nursing queue is empty</div>
            <p className="text-sm text-[#5C5652]">
              Patients allocated by the front office will appear here.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-[#E8E3D9]" data-testid="nursing-queue-list">
            {queue.map((p, i) => (
              <li
                key={p.id}
                className="flex items-center gap-4 px-6 py-4 hover:bg-[#F9F6F0] transition-colors"
              >
                <div className="h-10 w-10 rounded-full bg-[#E5A93D]/15 text-[#B57F1D] flex items-center justify-center font-bold">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#2A2624]">{p.name}</div>
                  <div className="text-xs text-[#5C5652]">
                    {p.age} · {p.gender} · {p.phone}
                  </div>
                </div>
                <div className="hidden sm:block text-right">
                  <div className="text-[10px] uppercase tracking-wider text-[#5C5652]">Token</div>
                  <div className="font-display text-forest">{p.op_token}</div>
                </div>
                <button
                  onClick={() => openVitals(p)}
                  data-testid={`open-vitals-${p.mrn}`}
                  className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-forest text-white text-sm font-semibold hover:bg-forest-hover transition-colors"
                >
                  Record vitals
                  <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Vitals modal */}
      {selected && (
        <Portal>
        <div
          className="fixed inset-0 bg-[#2A2624]/50 z-40 flex items-end sm:items-center justify-center p-0 sm:p-6"
          onClick={() => !busy && setSelected(null)}
          data-testid="vitals-overlay"
        >
          <div
            className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-lg border border-sand max-h-[92vh] overflow-y-auto animate-fade-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-sand flex items-start justify-between gap-4 sticky top-0 bg-white z-10">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5652] mb-1">
                  {selected.mrn} · Token {selected.op_token}
                </div>
                <div className="font-display text-2xl text-forest">{selected.name}</div>
                <div className="text-sm text-[#5C5652]">
                  {selected.age} · {selected.gender} · {selected.phone}
                </div>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="h-9 w-9 rounded-lg border border-sand hover:bg-[#F9F6F0] flex items-center justify-center"
                data-testid="vitals-close"
                aria-label="Close"
              >
                <X className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>

            <form onSubmit={submit} className="p-6 space-y-5" data-testid="vitals-form">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <NumField
                  testid="v-bp-sys"
                  label="BP Systolic"
                  suffix="mmHg"
                  value={form.bp_systolic}
                  onChange={(v) => setForm((f) => ({ ...f, bp_systolic: v }))}
                />
                <NumField
                  testid="v-bp-dia"
                  label="BP Diastolic"
                  suffix="mmHg"
                  value={form.bp_diastolic}
                  onChange={(v) => setForm((f) => ({ ...f, bp_diastolic: v }))}
                />
                <NumField
                  testid="v-pulse"
                  label="Pulse"
                  suffix="bpm"
                  value={form.pulse}
                  onChange={(v) => setForm((f) => ({ ...f, pulse: v }))}
                />
                <NumField
                  testid="v-resp"
                  label="Respiratory rate"
                  suffix="/min"
                  value={form.respiratory_rate}
                  onChange={(v) => setForm((f) => ({ ...f, respiratory_rate: v }))}
                />
                <NumField
                  testid="v-temp"
                  label="Temperature"
                  suffix="°C"
                  step="0.1"
                  value={form.temp_c}
                  onChange={(v) => setForm((f) => ({ ...f, temp_c: v }))}
                />
                <NumField
                  testid="v-spo2"
                  label="SpO₂"
                  suffix="%"
                  value={form.spo2}
                  onChange={(v) => setForm((f) => ({ ...f, spo2: v }))}
                />
                <NumField
                  testid="v-weight"
                  label="Weight"
                  suffix="kg"
                  step="0.1"
                  value={form.weight_kg}
                  onChange={(v) => setForm((f) => ({ ...f, weight_kg: v }))}
                />
                <NumField
                  testid="v-height"
                  label="Height"
                  suffix="cm"
                  value={form.height_cm}
                  onChange={(v) => setForm((f) => ({ ...f, height_cm: v }))}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C5652] mb-2">
                  Notes (optional)
                </label>
                <textarea
                  data-testid="v-notes"
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  rows={2}
                  placeholder="Anything the doctor should know before consultation"
                  className="w-full px-3 py-3 rounded-lg bg-white border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 focus:border-[#1A4331] transition-colors resize-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C5652] mb-2">
                  Assign to doctor · {branch.code}
                </label>
                {doctors.length === 0 ? (
                  <div className="p-4 rounded-lg bg-[#C25946]/8 border border-[#C25946]/30 text-sm text-[#8F3C2C]">
                    No doctors assigned to this branch. Ask the superadmin to map doctors.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="v-doctor-picker">
                    {doctors.map((d) => (
                      <button
                        type="button"
                        key={d.username}
                        data-testid={`v-doctor-${d.username}`}
                        onClick={() => setForm((f) => ({ ...f, assigned_doctor: d.username }))}
                      className={`text-left p-3 rounded-lg border-2 transition-all ${
                        form.assigned_doctor === d.username
                          ? "border-forest bg-forest text-white ring-2 ring-forest ring-offset-2 shadow-md"
                          : "border-sand bg-white hover:bg-[#F9F6F0] text-[#2A2624]"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="font-semibold">{d.name}</div>
                          <div className={`text-[10px] uppercase tracking-wider ${form.assigned_doctor === d.username ? "text-white/80" : "text-[#5C5652]"}`}>
                            {d.username}
                          </div>
                        </div>
                        {form.assigned_doctor === d.username && <span className="text-white text-lg leading-none">✓</span>}
                      </div>
                    </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="pt-2 flex gap-3 sticky bottom-0 bg-white -mx-6 px-6 py-4 border-t border-sand">
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="v-submit"
                  className="flex-1 h-11 rounded-lg bg-forest text-white font-semibold hover:bg-forest-hover transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
                >
                  {busy ? "Saving…" : "Save vitals & send to doctor"}
                  {!busy && <ArrowRight className="h-4 w-4" strokeWidth={1.75} />}
                </button>
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  disabled={busy}
                  className="h-11 px-5 rounded-lg border border-sand bg-white text-[#2A2624] font-semibold hover:bg-[#F9F6F0] transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
        </Portal>
      )}
    </div>
  );
}

const emptyForm = {
  bp_systolic: "",
  bp_diastolic: "",
  pulse: "",
  respiratory_rate: "",
  temp_c: "",
  spo2: "",
  weight_kg: "",
  height_cm: "",
  notes: "",
  assigned_doctor: "",
};

function NumField({ label, suffix, value, onChange, step = "1", testid }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C5652] mb-2">
        {label}
      </label>
      <div className="relative">
        <input
          data-testid={testid}
          type="number"
          step={step}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-11 pl-3 pr-14 rounded-lg bg-white border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 focus:border-[#1A4331] transition-colors"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-[#5C5652]">
          {suffix}
        </span>
      </div>
    </div>
  );
}

function StatMini({ label, value, icon: Icon }) {
  return (
    <div className="p-4 rounded-lg bg-white border border-sand">
      <div className="flex items-center gap-2 text-[#5C5652] text-[10px] font-bold uppercase tracking-[0.22em] mb-2">
        <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
        {label}
      </div>
      <div className="font-display text-3xl text-forest">{value}</div>
    </div>
  );
}
